import { AfterViewChecked, Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import dayjs from "dayjs";
import { Socket } from "ngx-socket-io";
import { v4 as uuidv4 } from 'uuid';
import { ChatDialogComponent } from "../../../../froexi_customerfacing/src/app/dialogs/chat-dialog/chat-dialog.component";
import { SubscriberParameterService } from "../../../../froexishared/src/services/SubscriberParameter.service";
import { GenericMessageDialogComponent } from "./dialog/generic-message-dialog/generic-message-dialog.component";
import { UserListDialogComponent } from "./dialog/user-list-dialog/user-list-dialog.component";
import { YesNoDialogComponent } from "./dialog/yes-no-dialog/yes-no-dialog.component";
import { ChatService } from "./services/chat.service";


@Component({
  selector: "app-chat-app",
  templateUrl: "./chat.component.html",
  styleUrls: ["./chat.component.scss"],
})
export class ChatComponent implements OnInit, AfterViewChecked, OnDestroy {

  @Input('platform') platform = 'FROhok';
  @ViewChild("focusMessageCtrl") focusMessageCtrl!: ElementRef;
  @ViewChild("scrollDown") scrollDown?: ElementRef<HTMLDivElement>;
  @ViewChild("customerScrollDown") customerScrollDown?: ElementRef<HTMLDivElement>;
  @ViewChild("backofficeScrollDown") backofficeScrollDown?: ElementRef<HTMLDivElement>;

  socket = inject(Socket)
  chatService = inject(ChatService)
  dialog = inject(MatDialog)
  messageCtrl: FormControl = new FormControl("");
  keyTranslated: string = ''
  reception: string = 'Hotel'
  now = dayjs();

  //#region -- fields customer-facing fields --
  reservationInfo: any = JSON.parse(sessionStorage.getItem("rinf") ?? "null")
  isChatVisibleForCustomerFacing: boolean = false;
  reOpenChat: string | null = localStorage.getItem('reOpenChat');
  customerFacingNameCtrl: FormControl = new FormControl('', [Validators.required])
  customerFacingChatData: IChatData[] = []
  customerFacingRoom_uuid: string = ''
  isWithin: boolean = false;
  customerHasMore: boolean = false;
  customerNextCursor: Date | string | null = null;
  customerChunkSize: number = 50;
  customerLoadingOlder: boolean = false;
  //#endregion

  //#region -- fields backoffice fields --
  userInfo: any =  JSON.parse(sessionStorage.getItem('userInfo') ?? "null")
  backofficeRooms: IRoomLike[] = [];
  previusRoom: IRoomLike | null = null
  backofficeChatData: IChatData[] = []
  backofficeRoom_uuid: string = ''
  messageCounterArr: IReadedCounter[] = []
  currentRoomData: any;
  backofficeHasMore: boolean = false;
  backofficeNextCursor: Date | string | null = null;
  backofficeChunkSize: number = 50;
  backofficeLoadingOlder: boolean = false;
  selectedBackofficeRoomUuid: string = '';
  backofficeRoomSearchCtrl: FormControl = new FormControl('');
  selectedRoom: any = JSON.parse(sessionStorage.getItem('selectedRoom')!);
  //#endregion

  //#region -- fields frohok fields --
  frohokUser: any = JSON.parse(sessionStorage.getItem('userInfo') ?? "null")
  frohokRooms: IFrohokRoom[] = []
  frohokData: IChatData[] = []
  usersHouseKeeping: string[] = []
  usersAll: string[] = []
  usersKarbantarto: string[] = []
  userIndex: number = -1;
  selectedFrohokRoomUuid: string = '';
  selectedFrohokRoomName: string = '';
  frohokHasMore: boolean = false;
  frohokNextCursor: Date | string | null = null;
  frohokChunkSize: number = 0;
  frohokLoadingOlder: boolean = false;
  frohokPaginationKnown: boolean = false;
  private frohokScrollPending = false;
  private pendingFrohokUnreadHighlightCount = 0;
  private frohokUnreadHighlightTimer: ReturnType<typeof setTimeout> | null = null;
  private socketListeners = new Map<string, (...args: any[]) => void>();
  receptionReadAt: Date | null = null;
  typingUsers: string[] = [];
  private typingUserTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private lastTypingEmitAt = 0;

  //#endregion

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: any,
    private dialogRef: MatDialogRef<ChatDialogComponent>,
    private translateService: TranslateService,
    private subscriberParameterService: SubscriberParameterService,
  ) { }

  ngOnInit(): void {
    this.keyTranslated = this.translateService.instant('chat.message_placeholder');
    this.reception = this.translateService.instant('chat.reception');

    // Minden uj socket kapcsolatnal visszaepitjuk az aktualis chat allapotot.
    this.registerSocketListener('connect', () => {
      this.rehydrateSocketStateAfterConnect();
    });

    // A readCounters sessionStorage-beli értékei csak a komponens életciklusán belül érvényesek.
    // Navigálás/frissítés után a szerver a hiteles forrás: az elavult offset eltüntetné az új olvasatlan üzeneteket.
    sessionStorage.removeItem('readCounters');
    this.messageCounterArr = [];

    if (this.backofficeRooms) {
      let temp = this.backofficeRooms;
      temp = temp.sort((a, b) => Number(b.message_counter) - Number(a.message_counter));
      this.backofficeRooms = temp;

    }

    // Az aktív szoba UUID nem kerül inicializálásra az init során.
    // Csak akkor lesz aktív, ha a felhasználó rákattint egy szobára.
    this.selectedBackofficeRoomUuid = '';


    //#region - customer-facing --
    this.checkCustomerFacing()

    if (this.isChatVisibleForCustomerFacing) {
      const guestUuid = this.getGuestUuid();
      if (guestUuid) {
        this.maximized(guestUuid);
      }
    }

    this.registerSocketListener('chatData', (data: IChatData[]) => {
      // Csak akkor jelenítjük meg a chat előzményeket, ha aktív időszak van.
      if (this.chatService.isWithin()) {
        this.customerFacingChatData = data
        this.chatService.syncCustomerFacingCounterFromCurrentChat(this.customerFacingChatData.length);
        this.scrollToBottom('customer-facing');
      }
    })

    this.registerSocketListener('messageReadByReception', (payload: IMessageReadByReception) => {
      if (this.platform !== 'customer-facing') {
        return;
      }
      const guestUuid = this.getGuestUuid();
      if (!guestUuid || guestUuid !== payload?.room_uuid) {
        return;
      }
      // Csak az első backoffice felhasználó által olvasott üzenetet mutatjuk
      if (this.receptionReadAt === null) {
        this.receptionReadAt = payload.read_at ? new Date(payload.read_at) : new Date();
      }
    })
    //#endregion

    //#region -- backoffice --
    this.checkBackoffice()
    this.registerSocketListener('selectedRoomsByHotel', (response: IRoomLike[]) => {
      this.backofficeRooms = this.sortBackofficeRoomsByUnread(response);

      this.backofficeRooms.forEach(element => {
        const counter = this.messageCounterArr.find(item => item.room_uuid === element.room_uuid)
        if (counter) {
          element.counter = counter.counter;
        }
      })

      // Megtartjuk az aktív szoba UUID-jét az újrarendezés után
      const activeRoomUuid = this.selectedBackofficeRoomUuid;
      this.syncBackofficeBadgeVisibility(activeRoomUuid);
    })
    this.registerSocketListener('backofficeChatData', (result: IChatData[]) => {
      this.backofficeChatData = result
      this.scrollToBottom('backoffice');
    })
    this.registerSocketListener('room_datas', (result: any[]) => {
      this.currentRoomData = result[0]
    })
    this.registerSocketListener('roomDeleted', () => {
      localStorage.removeItem('reOpenChat');
      this.clearGuestUuid();
      localStorage.removeItem('customerFacingNameCtrl');
    })
    this.registerSocketListener('deletedFromBackoffice', (response: unknown) => {
      //alert(response)
      this.dialog.open(GenericMessageDialogComponent, {
        disableClose: true,
        hasBackdrop: true,
        width: "auto",
        data: { messageText: this.translateService.instant('chat.information_about_deletion'), messageType: 'INFORMATION', showIcon: true }
      })
    })
    this.registerSocketListener('newMessageCounter', (response: { room_uuid: string; message_counter: number | string }) => {
      const room = this.backofficeRooms.find(item => item.room_uuid === response.room_uuid);
      if (!room) {
        return;
      }

      const selectedRoomUuid = this.getSelectedBackofficeRoomUuid();
      if (selectedRoomUuid && selectedRoomUuid === response.room_uuid) {
        room.message_counter = 0;
      } else {
        room.message_counter = Number(response.message_counter) || 0;
      }

      this.backofficeRooms = this.sortBackofficeRoomsByUnread(this.backofficeRooms);
      this.syncBackofficeBadgeVisibility(selectedRoomUuid);
    })

    this.registerSocketListener('messageChunkMeta', (meta: IMessageChunkMeta) => {
      if (meta.target === 'customer-facing') {
        this.customerHasMore = !!meta.has_more;
        this.customerNextCursor = meta.next_cursor;
        this.customerChunkSize = Number(meta.chunk_size) || this.customerChunkSize;
        return;
      }

      if (meta.target === 'backoffice') {
        this.backofficeHasMore = !!meta.has_more;
        this.backofficeNextCursor = meta.next_cursor;
        this.backofficeChunkSize = Number(meta.chunk_size) || this.backofficeChunkSize;
        return;
      }

      if (meta.target === 'FROhok') {
        this.frohokHasMore = !!meta.has_more;
        this.frohokNextCursor = meta.next_cursor;
        this.frohokPaginationKnown = true;
      }
    })

    this.registerSocketListener('olderMessagesChunk', (payload: IOlderMessagesChunk) => {
      if (payload.target === 'customer-facing') {
        this.prependMessagesWithScroll('customer-facing', payload.rows);
        this.customerHasMore = !!payload.has_more;
        this.customerNextCursor = payload.next_cursor;
        this.customerChunkSize = Number(payload.chunk_size) || this.customerChunkSize;
        this.customerLoadingOlder = false;
        return;
      }

      if (payload.target === 'backoffice') {
        this.prependMessagesWithScroll('backoffice', payload.rows);
        this.backofficeHasMore = !!payload.has_more;
        this.backofficeNextCursor = payload.next_cursor;
        this.backofficeChunkSize = Number(payload.chunk_size) || this.backofficeChunkSize;
        this.backofficeLoadingOlder = false;
        return;
      }

      if (payload.target === 'FROhok') {
        this.prependMessagesWithScroll('FROhok', payload.rows);
        this.frohokHasMore = !!payload.has_more;
        this.frohokNextCursor = payload.next_cursor;
        this.frohokLoadingOlder = false;
        this.frohokPaginationKnown = true;
      }
    })

    this.registerSocketListener('chatMessageDelta', (payload: IMessageDelta) => {
      if (!payload?.message || !payload?.room_uuid) {
        return;
      }

      if (this.platform === 'customer-facing') {
        const guestUuid = this.getGuestUuid();
        if (guestUuid !== payload.room_uuid) {
          return;
        }

        if (payload.message.sender === this.customerFacingNameCtrl.value) {
          this.receptionReadAt = null;
        }

        // Race condition védelem: chatData (maximized válasz) és chatMessageDelta
        // párhuzamosan érkezhet ugyanarra az üzenetre. Timestamp+sender+chat
        // alapján szűrünk duplikátumra, mivel a room_data táblának nincs PK-ja a payload-ban.
        const isDuplicate = this.customerFacingChatData.some(m =>
          new Date(m.chat_timestamp).getTime() === new Date(payload.message.chat_timestamp).getTime()
          && m.sender === payload.message.sender
          && m.chat === payload.message.chat
        );
        if (!isDuplicate) {
          this.customerFacingChatData = [...this.customerFacingChatData, payload.message];
          this.scrollToBottom('customer-facing');
        }
        return;
      }

      if (this.platform === 'backoffice') {
        const selectedRoom = sessionStorage.getItem('selectedRoom');
        const parsedRoom: IRoomLike = selectedRoom ? JSON.parse(selectedRoom) : {};
        if (!parsedRoom?.room_uuid || parsedRoom.room_uuid !== payload.room_uuid) {
          return;
        }

        this.backofficeChatData = [...this.backofficeChatData, payload.message];
        this.scrollToBottom('backoffice');
      }
    })

    this.registerSocketListener('typingStatus', (payload: ITypingStatus) => {
      this.handleTypingStatus(payload);
    })
    //#endregion

    // #region -- frohok --
    this.checkFROhok()

    this.registerSocketListener('housekeeping', (request: unknown) => {
      this.syncFrohokUsers('housekeeping', request)
    })
    this.registerSocketListener('all', (request: unknown) => {
      this.syncFrohokUsers('all', request)
    })
    this.registerSocketListener('karbantarto', (request: unknown) => {
      this.syncFrohokUsers('karbantarto', request)
    })
    this.registerSocketListener('frohokPresence', (request: { housekeeping?: unknown; all?: unknown; karbantarto?: unknown } | null | undefined) => {
      this.applyFrohokPresence(request)
    })

    this.registerSocketListener('frohokData', (request: unknown) => {
      this.frohokData = Array.isArray(request) ? (request as IChatData[]) : [];
      this.applyFrohokUnreadHighlights();
      if (this.frohokData.length && !this.frohokNextCursor) {
        this.frohokNextCursor = this.frohokData[0].chat_timestamp;
      }
      this.frohokScrollPending = true;
      this.frohokLoadingOlder = false;
    })

    this.registerSocketListener('frohokMessageDelta', (payload: IMessageDelta) => {
      if (this.platform !== 'FROhok' || !payload?.message || !payload?.room_uuid) {
        return;
      }

      if (!this.selectedFrohokRoomUuid || this.selectedFrohokRoomUuid !== payload.room_uuid) {
        return;
      }

      const currentMessages = Array.isArray(this.frohokData) ? this.frohokData : [];
      const shouldAutoScroll = this.isNearBottom(this.scrollDown?.nativeElement);
      this.frohokData = [...currentMessages, payload.message];
      this.frohokScrollPending = shouldAutoScroll;
    })

    this.registerSocketListener('frohokError', (errorMsg: string) => {
      this.frohokLoadingOlder = false;
      console.error('[FROhok error]', errorMsg);
      this.dialog.open(GenericMessageDialogComponent, {
        disableClose: false,
        hasBackdrop: true,
        width: 'auto',
        data: { messageText: errorMsg, messageType: 'ERROR', showIcon: true }
      });
    })

    // #endregion -- frohok --

  }

  ngOnDestroy(): void {
    this.clearBackofficeActiveRoom();

    this.socketListeners.forEach((callback, event) => {
      this.socket.removeListener(event, callback);
    });
    this.socketListeners.clear();
    this.clearFrohokUnreadHighlights();
    this.clearTypingUsers();
  }

  private clearBackofficeActiveRoom(): void {
    if (this.platform !== 'backoffice') {
      return;
    }

    const selectedRoom = this.getSelectedBackofficeRoom();
    if (selectedRoom?.room_uuid && selectedRoom?.room_name) {
      this.socket.emit('leaveChatRoom', {
        room_uuid: selectedRoom.room_uuid,
        room_name: selectedRoom.room_name,
      });
    }

    sessionStorage.removeItem('readCounters');

    this.selectedBackofficeRoomUuid = '';
    this.previusRoom = null;
    this.currentRoomData = null;
    sessionStorage.removeItem('selectedRoom');
  }

  private registerSocketListener(event: string, callback: (...args: any[]) => void): void {
    // Remove previous listener for this event if component re-initializes
    if (this.socketListeners.has(event)) {
      this.socket.removeListener(event, this.socketListeners.get(event)!);
    }
    this.socketListeners.set(event, callback);
    this.socket.on(event, callback);
  }



  ngAfterViewChecked(): void {
    if (!this.frohokScrollPending || this.platform !== 'FROhok') {
      return;
    }

    const chatContainer = this.scrollDown?.nativeElement;
    if (!chatContainer) {
      return;
    }

    chatContainer.scrollTop = chatContainer.scrollHeight;
    this.frohokScrollPending = false;
  }

  /**
   * Socket reconnect utan platformfuggoen ujraepiti a szerveroldali room membershipet es session allapotot.
   */
  private rehydrateSocketStateAfterConnect(): void {
    if (this.platform === 'customer-facing') {
      this.rehydrateCustomerFacingOnConnect();
      return;
    }

    if (this.platform === 'backoffice') {
      this.rehydrateBackofficeOnConnect();
      return;
    }

    if (this.platform === 'FROhok') {
      this.rehydrateFrohokOnConnect();
    }
  }

  /**
   * Customer-facing reconnect utan ujra lekéri az aktiv szoba legfrissebb allapotat.
   */
  private rehydrateCustomerFacingOnConnect(): void {
    const guestUuid = this.getGuestUuid();
    if (!guestUuid) {
      return;
    }

    this.maximized(guestUuid);
  }

  /**
   * Backoffice reconnect utan ujrainicializalja a room listat es visszalep az aktiv szobaba.
   */
  private rehydrateBackofficeOnConnect(): void {
    if (!this.userInfo?.Name || !this.userInfo?.SubscriberTree?.SubscriberCode || this.userInfo?.SubscriberTree?.Htlnum == null) {
      return;
    }

    const initRequest = {
      name: this.userInfo.Name,
      subscriber: this.userInfo.SubscriberTree.SubscriberCode,
      hotel_id: this.userInfo.SubscriberTree.Htlnum,
    };
    this.socket.emit('receptionist_init', initRequest);

    const selectedRoom = this.getSelectedBackofficeRoom();
    if (!selectedRoom?.room_uuid || !selectedRoom?.room_name) {
      return;
    }

    this.selectedBackofficeRoomUuid = selectedRoom.room_uuid || '';
    this.previusRoom = selectedRoom;

    this.socket.emit('joinChatRoom', {
      room: selectedRoom,
      previusRoom: '',
      chunk_size: this.backofficeChunkSize,
    });
  }

  /**
   * FROhok reconnect utan ujrainicializalja a presence/lista allapotot es visszalep a korabban aktiv szobaba.
   */
  private rehydrateFrohokOnConnect(): void {
    if (!this.frohokUser?.SubscriberTree?.SubscriberCode || this.frohokUser?.SubscriberTree?.Htlnum == null || !this.frohokUser?.Name) {
      return;
    }

    const initRequest = {
      room_type: 'FROhok',
      subscriber: this.frohokUser.SubscriberTree.SubscriberCode,
      hotel_id: this.frohokUser.SubscriberTree.Htlnum,
      guest_name: this.frohokUser.Name,
    };
    this.socket.emit('frohokInit', initRequest);

    if (!this.selectedFrohokRoomUuid) {
      return;
    }

    const selectedRoomName = this.selectedFrohokRoomName
      || this.frohokRooms.find(room => room.room_uuid === this.selectedFrohokRoomUuid)?.room_name
      || '';

    if (!selectedRoomName) {
      return;
    }

    this.socket.emit('selectRoomByIndexFromFrohok', {
      room_uuid: this.selectedFrohokRoomUuid,
      room_name: selectedRoomName,
      room_type: 'FROhok',
      subscriber: this.frohokUser.SubscriberTree.SubscriberCode,
      hotel_id: this.frohokUser.SubscriberTree.Htlnum,
      guest_name: this.frohokUser.Name,
    });
  }

  //#region -- methods FROhok --
  get selectedFrohokRoom(): IFrohokRoom | undefined {
    return this.frohokRooms.find(r => r.room_uuid === this.selectedFrohokRoomUuid);
  }

  compareRooms = (r1: IFrohokRoom | null, r2: IFrohokRoom | null): boolean => {
    return r1?.room_uuid === r2?.room_uuid;
  }

  trackByRoomUuid = (index: number, room: IFrohokRoom): string => {
    return room.room_uuid;
  }

  checkFROhok() {
    if (this.platform === 'FROhok') {
      let initRequest = {
        room_type: 'FROhok',
        subscriber: this.frohokUser.SubscriberTree.SubscriberCode,
        hotel_id: this.frohokUser.SubscriberTree.Htlnum,
        guest_name: this.frohokUser.Name,
      }
      this.socket.emit('frohokInit', initRequest)

      this.registerSocketListener('roomsByHotel', (response: IFrohokRoom[]) => {
        // A szerver payloadot nem mutaljuk kozvetlenul, hogy a topbar es mas listenerek
        // konzisztens, valtozatlan unread adatokkal dolgozzanak.
        this.frohokRooms = (response || []).map(room => ({ ...room }));
        // Az aktív (kiválasztott) room badge-ét elrejtjük
        if (this.selectedFrohokRoomUuid) {
          const activeRoom = this.frohokRooms.find(r => r.room_uuid === this.selectedFrohokRoomUuid);
          if (activeRoom) {
            activeRoom.message_counter = 0;
          }
        }
      })
    }
  }

  selectedRoomByIndexFrohok(room: IFrohokRoom) {
    if (!room || !room.room_uuid) {
      return;
    }
    this.clearFrohokUnreadHighlights();
    // Megkeressük az aktuális indexet UUID alapján, hogy ne függünk a sorrendtől
    const foundIndex = this.frohokRooms.findIndex(r => r.room_uuid === room.room_uuid);
    if (foundIndex >= 0) {
      this.userIndex = foundIndex;
    }
    // Hozzáadjuk a guest_name-hez a bejelentkezett user nevét
    const rawUserInfo = sessionStorage.getItem("userInfo");
    const parsedUserInfo = rawUserInfo ? JSON.parse(rawUserInfo) : null;
    room.guest_name = parsedUserInfo?.Name ?? "";
    this.selectedFrohokRoomUuid = room.room_uuid;
    this.selectedFrohokRoomName = room.room_name;
    this.frohokHasMore = false;
    this.frohokNextCursor = null;
    this.frohokLoadingOlder = false;
    this.frohokPaginationKnown = false;
    this.clearTypingUsers();
    // Topbar badge azonnali frissítése (optimista update, még a szerver válasz előtt)
    // Snapshotot kuldunk, hogy a tovabbi lokalis mutaciok ne irjak felul az event adatat.
    this.chatService.emitFrohokRoomSelected({
      room_uuid: room.room_uuid,
      message_counter: Number(room.message_counter) || 0,
    });
    // Az aktív room badge-ét azonnal nullázzuk a kliens oldalon
    const previousMessageCount = Number(room.message_counter) || 0;
    this.pendingFrohokUnreadHighlightCount = Math.max(previousMessageCount, 0);
    room.message_counter = 0;
    this.socket.emit('selectRoomByIndexFromFrohok', {
      ...room,
    });
  }

  /**
   * A megnyitott FROhok szobában az olvasatlan üzeneteket 2 másodpercre sötétebb háttérrel emeli ki.
   */
  /**
   * Visszaadja az első olvasatlan üzenet index-ét a FROhok chat-ban.
   * Ha nincs olvasatlan üzenet, -1-et ad vissza.
   */
  getFirstUnreadFrohokMessageIndex(): number {
    if (!Array.isArray(this.frohokData) || !this.frohokData.length) {
      return -1;
    }

    const firstIndex = this.frohokData.findIndex(msg => msg.frohokUnreadHighlight === true);
    return firstIndex;
  }

  private applyFrohokUnreadHighlights(): void {
    if (!Array.isArray(this.frohokData) || !this.frohokData.length) {
      this.pendingFrohokUnreadHighlightCount = 0;
      return;
    }

    const unreadCount = Math.max(this.pendingFrohokUnreadHighlightCount, 0);
    if (unreadCount === 0) {
      return;
    }

    const highlightCount = Math.min(unreadCount, this.frohokData.length);
    const startIndex = this.frohokData.length - highlightCount;

    for (let i = startIndex; i < this.frohokData.length; i++) {
      this.frohokData[i].frohokUnreadHighlight = true;
    }

    if (this.frohokUnreadHighlightTimer) {
      clearTimeout(this.frohokUnreadHighlightTimer);
    }

    this.frohokUnreadHighlightTimer = setTimeout(() => {
      this.frohokData.forEach(message => {
        if (message.frohokUnreadHighlight) {
          message.frohokUnreadHighlight = false;
        }
      });
      this.frohokUnreadHighlightTimer = null;
    }, 2000);

    this.pendingFrohokUnreadHighlightCount = 0;
  }

  /**
   * Takarítja az ideiglenes FROhok olvasatlan kiemeléseket és az időzítőt.
   */
  private clearFrohokUnreadHighlights(): void {
    if (this.frohokUnreadHighlightTimer) {
      clearTimeout(this.frohokUnreadHighlightTimer);
      this.frohokUnreadHighlightTimer = null;
    }

    if (!Array.isArray(this.frohokData) || !this.frohokData.length) {
      return;
    }

    this.frohokData.forEach(message => {
      if (message.frohokUnreadHighlight) {
        message.frohokUnreadHighlight = false;
      }
    });
  }

  private syncFrohokUsers(group: 'housekeeping' | 'all' | 'karbantarto', request: unknown): void {
    const normalizedUsers = this.normalizeFrohokUsers(request);
    const normalizedUserSet = new Set(normalizedUsers.map(u => u.toLowerCase()));

    if (group === 'housekeeping') {
      this.usersHouseKeeping = normalizedUsers;
      this.usersAll = this.usersAll.filter(user => !normalizedUserSet.has(user.toLowerCase()));
      this.usersKarbantarto = this.usersKarbantarto.filter(user => !normalizedUserSet.has(user.toLowerCase()));
      return;
    }

    if (group === 'all') {
      this.usersAll = normalizedUsers;
      this.usersHouseKeeping = this.usersHouseKeeping.filter(user => !normalizedUserSet.has(user.toLowerCase()));
      this.usersKarbantarto = this.usersKarbantarto.filter(user => !normalizedUserSet.has(user.toLowerCase()));
      return;
    }

    this.usersKarbantarto = normalizedUsers;
    this.usersHouseKeeping = this.usersHouseKeeping.filter(user => !normalizedUserSet.has(user.toLowerCase()));
    this.usersAll = this.usersAll.filter(user => !normalizedUserSet.has(user.toLowerCase()));
  }

  private applyFrohokPresence(request: { housekeeping?: unknown; all?: unknown; karbantarto?: unknown } | null | undefined): void {
    this.usersHouseKeeping = [];
    this.usersAll = [];
    this.usersKarbantarto = [];

    this.syncFrohokUsers('housekeeping', request?.housekeeping);
    this.syncFrohokUsers('all', request?.all);
    this.syncFrohokUsers('karbantarto', request?.karbantarto);
  }

  private normalizeFrohokUsers(request: unknown): string[] {
    if (!Array.isArray(request)) {
      return [];
    }

    const seenUsers = new Set<string>();

    return request.reduce((users: string[], item: unknown) => {
      if (typeof item !== 'string') {
        return users;
      }

      const displayName = item.trim();
      const dedupeKey = displayName.toLowerCase();
      if (!dedupeKey || seenUsers.has(dedupeKey)) {
        return users;
      }

      seenUsers.add(dedupeKey);
      users.push(displayName);
      return users;
    }, []);
  }

  openUserListDialog(index: number) {
    this.userIndex = index;
    let data: string[] = [];

    if (index === 0) data = this.usersHouseKeeping;
    if (index === 1) data = this.usersAll;
    if (index === 2) data = this.usersKarbantarto;

    this.dialog.open(UserListDialogComponent, {
      data,
      minWidth: '80vw',
      minHeight: '50vh',
      panelClass: 'user-group-dialog'
    });
  }
  //#endregion

  //#region -- methods costumer-facing --
  checkCustomerFacing(): void {
    if (this.platform === 'customer-facing') {

      localStorage.setItem('reOpenChat', JSON.stringify(this._data.isMinimized));
      if (this._data.isMinimized) {
        this.isChatVisibleForCustomerFacing = true;
        this.customerFacingNameCtrl.setValue(localStorage.getItem('customerFacingNameCtrl'))
      }
    }
  }

  minimizeDialog(): void {
    localStorage.setItem('reOpenChat', 'true')
    this.chatService.syncCustomerFacingCounterFromCurrentChat(this.customerFacingChatData.length);
    this.dialogRef.close(this.customerFacingChatData?.length)
  }

  createRoom(): void {
    this.isChatVisibleForCustomerFacing = true;

    let guestUuid = uuidv4();
    this.setGuestUuid(guestUuid);
    localStorage.setItem('customerFacingNameCtrl', this.customerFacingNameCtrl.value);
    localStorage.setItem('reOpenChat', 'true');
    let sgd = JSON.parse(sessionStorage.getItem('sgd') ?? "{}");
    let rinf = JSON.parse(sessionStorage.getItem('rinf') ?? "{}");
    let resnum: any = sessionStorage.getItem('resnum');

    let request: ICreateRoom = {
      room_uuid: guestUuid,
      room_name: this.normalizedChatName(this.customerFacingNameCtrl),
      room_type: 'FROexi',
      subscriber: this.reservationInfo.Subscriber,
      hotel_id: this.reservationInfo.Hotelid,
      guest_name: this.customerFacingNameCtrl.value,
      reservationsGuestname: sgd.Name,
      reservationsRoomid: rinf.ReservationUuId,
      reservationsArrival: sgd.Arrival,
      reservationsDeparture: sgd.Departure,
      resnum: resnum
    }
    // Új szoba - resetáljuk az olvasási nyugtát
    this.receptionReadAt = null;
    this.chatService.syncCustomerFacingCounterFromCurrentChat(0);
    this.socket.emit('createRoom', request)
  }

  maximized(guestUuid: string): void {
    let request = {
      room_uuid: guestUuid,
      room_name: this.normalizedChatName(this.customerFacingNameCtrl),
      chunk_size: this.customerChunkSize,
    }

    this.socket.emit('maximized', request)
  }

  private normalizedChatName(formControlName: FormControl): string {
    return (
      formControlName.value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .split(" ")
        .join("_") +
      "_szoba"
    );
  }
  //#endregion

  //#region -- methods backoffice --
  checkBackoffice(): void {
    if (this.platform === 'backoffice') {
      let request = {
        name: this.userInfo.Name,
        subscriber: this.userInfo.SubscriberTree.SubscriberCode,
        hotel_id: this.userInfo.SubscriberTree.Htlnum,
      }
      this.socket.emit('receptionist_init', request)
    }
  }

  joinChatRoom(room: IRoomLike, index: number): void {
    this.clearTypingUsers();
    
    // Az aktuális szoba message_counter-ét 0-ra állítjuk
    const previousMessageCount = Number(room.message_counter) || 0;
    room.message_counter = 0;
    
    // Frissítjük az messageCounterArr-t az aktuális szoba UUID-je alapján
    // így a selectedRoomsByHotel listener után helyesen számolódik az unread
    const existingCounter = this.messageCounterArr.find(item => item.room_uuid === room.room_uuid);
    if (existingCounter) {
      existingCounter.counter = String(previousMessageCount);
    } else {
      this.messageCounterArr.push({ room_uuid: room.room_uuid || '', counter: String(previousMessageCount) });
    }
    sessionStorage.setItem('readCounters', JSON.stringify(this.messageCounterArr));
    
    sessionStorage.setItem('selectedRoom', JSON.stringify(room));
    // Tároljuk az aktív szoba UUID-jét az active class megtartásához
    this.selectedBackofficeRoomUuid = room.room_uuid || '';

    if (this.previusRoom === null) {
      let request = {
        room: room,
        previusRoom: '',
        chunk_size: this.backofficeChunkSize,
      }
      this.socket.emit('joinChatRoom', request)
    } else if (this.previusRoom !== room) {

      // this.previusRoom.counter = this.previusRoom.message_counter || 0;

      // let counter = this.messageCounterArr.find(item => item.room_uuid === this.previusRoom.room_uuid);
      // if (counter) {
      //   counter.counter = this.previusRoom.counter
      // } else {
      //   this.messageCounterArr.push({ room_uuid: this.previusRoom.room_uuid, counter: this.previusRoom.counter })
      // }
      // sessionStorage.setItem('readCounters', JSON.stringify(this.messageCounterArr))

      let request = {
        room: room,
        previusRoom: this.previusRoom.room_name,
        chunk_size: this.backofficeChunkSize,
      }
      this.socket.emit('joinChatRoom', request);
    }

    this.backofficeRooms = this.sortBackofficeRoomsByUnread(this.backofficeRooms);
    this.syncBackofficeBadgeVisibility(room.room_uuid || '');
    this.previusRoom = room;
    if (this.currentRoomData) {
      this.currentRoomData.resnum = room.resnum
    }
  }

  isBefore(day1: dayjs.ConfigType, day2: dayjs.ConfigType): boolean {
    return dayjs(day1).isBefore(day2)
  }


  deleteRoomById(index: number, room: IRoomLike) {
    this.subscriberParameterService.getServerTime().subscribe((data: number) => {

      let departureTime = dayjs(room.reservation_departure);
      let serverTimeDayjs = dayjs.unix(data);

      if (serverTimeDayjs.isAfter(departureTime)) {
        this.dialog.open(YesNoDialogComponent,
          { data: { messageText: '', messageType: 'INFORMATION' } }).afterClosed().subscribe(data => {
            if (data) {
              this.socket.emit('deleteChatRoom', room)
              this.backofficeChatData = [];
            }
          })
      }
    })
  }
  //#endregion

  //#region -- methods message --
  sendMessage() {
    let sendMessage: any = {}

    // Customerfacing
    if (this.platform === 'customer-facing') {
      this.receptionReadAt = null;
      sendMessage = {
        room_uuid: this.getGuestUuid(),
        chat: this.messageCtrl.value,
        sender: this.customerFacingNameCtrl.value,
        platform: 'customer-facing'
      };
    }

    // Bakcoffice
    if (this.platform === 'backoffice') {
      const selectedRoom = sessionStorage.getItem('selectedRoom');
      const parsedRoom: IRoomLike = selectedRoom ? JSON.parse(selectedRoom) : {};
      sendMessage = {
        room_uuid: parsedRoom.room_uuid ?? '',
        chat: this.messageCtrl.value,
        sender: this.userInfo?.Name ?? '',
        platform: 'backoffice'
      };
    }

    if (this.platform === 'FROhok') {
      const fallbackRoom: IRoomLike = this.frohokRooms[this.userIndex] || {};
      const rawUserInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = rawUserInfo ? JSON.parse(rawUserInfo) : null;
      sendMessage = {
        room_uuid: this.selectedFrohokRoomUuid || fallbackRoom.room_uuid || '',
        room_name: this.selectedFrohokRoomName || fallbackRoom.room_name || '',
        chat: this.messageCtrl.value,
        sender: parsedUserInfo?.Name ?? '',
        platform: 'FROhok'
      };
    }
    this.socket.emit('message', sendMessage)
    this.messageCtrl.setValue("");
    this.focusMessageCtrl.nativeElement.focus();
  }

  onMessageInputChange(): void {
    const payload = this.buildTypingPayload();
    if (!payload || !payload.chat?.trim()) {
      return;
    }

    const nowMs = Date.now();
    if (nowMs - this.lastTypingEmitAt < 400) {
      return;
    }

    this.lastTypingEmitAt = nowMs;
    this.socket.emit('typing', payload);
  }

  get typingIndicatorText(): string {
    if (!this.typingUsers.length) {
      return '';
    }

    if (this.platform === 'customer-facing') {
      return `${this.reception} épp üzenetet ír...`;
    }

    return `${this.typingUsers.join(', ')} épp üzenetet ír...`;
  }

  sendMessageOnEnter(event: KeyboardEvent) {
    if (this.messageCtrl.value === "") {
      this.focusMessageCtrl.nativeElement.focus();
      return
    }
    if (event!.key === "Enter") this.sendMessage();
  }

  sendMessageOnClick() {
    this.sendMessage();
  }

  cancel() {
    this.dialogRef.close(false)
  }
  //#endregion

  getBagdeNumber(room: IRoomLike): number {
    return (Number(room.message_counter) || 0) - (Number(room.counter) || 0);
  }

  get filteredActiveBackofficeRooms(): IRoomLike[] {
    return this.backofficeRooms.filter(room => this.isBackofficeRoomActive(room) && this.matchesBackofficeRoomSearch(room));
  }

  get filteredInactiveBackofficeRooms(): IRoomLike[] {
    return this.backofficeRooms.filter(room => !this.isBackofficeRoomActive(room) && this.matchesBackofficeRoomSearch(room));
  }

  shouldShowSender(messages: IChatData[] | null | undefined, index: number): boolean {
    if (!messages || index <= 0) {
      return true;
    }

    const previousMessage = messages[index - 1];
    const currentMessage = messages[index];

    if (!previousMessage || !currentMessage) {
      return true;
    }

    return previousMessage.sender !== currentMessage.sender;
  }

  get lastGuestMessageIndex(): number {
    if (!this.customerFacingChatData?.length) {
      return -1;
    }

    const lastVisibleMessage = [...this.customerFacingChatData]
      .reverse()
      .find(message => message?.sender !== 'Admin');
    if (!lastVisibleMessage || lastVisibleMessage.sender !== this.customerFacingNameCtrl.value) {
      return -1;
    }

    const guestName = this.customerFacingNameCtrl.value;
    for (let i = this.customerFacingChatData.length - 1; i >= 0; i--) {
      const msg = this.customerFacingChatData[i];
      if (msg.sender !== 'Admin' && msg.sender === guestName) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Customer-facing bal oldali üzeneteknél: a "Recepció" fejlécet csak akkor mutatjuk,
   * ha az előző látható (nem-Admin) üzenet a vendégtől érkezett.
   * Így az összes backoffice küldő egyetlen "Recepció" csoport alá esik.
   */
  shouldShowReceptionHeader(messages: IChatData[] | null | undefined, index: number): boolean {
    if (!messages || index <= 0) {
      return true;
    }

    for (let j = index - 1; j >= 0; j--) {
      const prev = messages[j];
      if (prev.sender === 'Admin') {
        continue;
      }
      // Ha az előző látható üzenet a vendégtől volt, mutasd a "Recepció" fejlécet
      return prev.sender === this.customerFacingNameCtrl.value;
    }

    return true;
  }

  private getSelectedBackofficeRoom(): IRoomLike {
    const selectedRoom = sessionStorage.getItem('selectedRoom');
    return selectedRoom ? JSON.parse(selectedRoom) : {};
  }

  private getSelectedBackofficeRoomUuid(): string {
    // Elsősorban az aktív szoba UUID-jét használjuk, amely a felhasználó interakciói során frissül
    if (this.selectedBackofficeRoomUuid) {
      return this.selectedBackofficeRoomUuid;
    }
    // Fallback a sessionStorage-ba, ha az inicializáció során nincs még beállítva
    return this.getSelectedBackofficeRoom()?.room_uuid || '';
  }

  private syncBackofficeBadgeVisibility(selectedRoomUuid: string): void {
    this.backofficeRooms = this.backofficeRooms.map(room => ({
      ...room,
      hiddenBagde: !!selectedRoomUuid && room.room_uuid === selectedRoomUuid,
    }));
  }

  /**
   * A backoffice szobalistat konzisztensen, csokkeno unread sorrendben adja vissza.
   */
  private sortBackofficeRoomsByUnread(rooms: IRoomLike[]): IRoomLike[] {
    return [...(rooms || [])].sort((a: IRoomLike, b: IRoomLike) => {
      return (Number(b?.message_counter) || 0) - (Number(a?.message_counter) || 0);
    });
  }

  private moveBackofficeRoomToTop(roomUuid: string): void {
    if (!roomUuid || !Array.isArray(this.backofficeRooms) || this.backofficeRooms.length < 2) {
      return;
    }

    const roomIndex = this.backofficeRooms.findIndex(room => room.room_uuid === roomUuid);
    if (roomIndex <= 0) {
      return;
    }

    const [targetRoom] = this.backofficeRooms.splice(roomIndex, 1);
    this.backofficeRooms = [targetRoom, ...this.backofficeRooms];
    this.syncBackofficeBadgeVisibility(this.getSelectedBackofficeRoomUuid());
  }

  private isBackofficeRoomActive(room: IRoomLike): boolean {
    const departureDate = dayjs(room?.reservation_departure);
    if (!departureDate.isValid()) {
      return false;
    }

    // Aktívnak tekintjük a szobát a departure nap végéig.
    return !departureDate.endOf('day').isBefore(dayjs());
  }

  private matchesBackofficeRoomSearch(room: IRoomLike): boolean {
    const searchTerm = this.normalizeBackofficeSearchValue(this.backofficeRoomSearchCtrl.value);
    if (!searchTerm) {
      return true;
    }

    const roomName = this.normalizeBackofficeSearchValue(room?.room_name);
    const guestName = this.normalizeBackofficeSearchValue(room?.guest_name);
    return roomName.includes(searchTerm) || guestName.includes(searchTerm);
  }

  private normalizeBackofficeSearchValue(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toLowerCase();
  }

  private normalizeIdentity(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private sameIdentity(left: unknown, right: unknown): boolean {
    const normalizedLeft = this.normalizeIdentity(left);
    const normalizedRight = this.normalizeIdentity(right);

    return !!normalizedLeft && normalizedLeft === normalizedRight;
  }

  isBackofficeGuestMessage(chat: IChatData): boolean {
    const selectedRoom = this.getSelectedBackofficeRoom();
    const possibleGuestNames = [
      selectedRoom?.guest_name,
      this.currentRoomData?.reservation_guestname,
    ];

    return possibleGuestNames.some(name => this.sameIdentity(chat?.sender, name));
  }

  isBackofficeOwnReceptionMessage(chat: IChatData): boolean {
    return this.sameIdentity(chat?.sender, this.userInfo?.Name);
  }

  onChatScroll(target: 'customer-facing' | 'backoffice' | 'FROhok', event: Event): void {
    const chatContainer = event.target as HTMLElement;
    if (!chatContainer || chatContainer.scrollTop > 24) {
      return;
    }

    if (target === 'customer-facing') {
      this.loadOlderCustomerMessages();
      return;
    }

    if (target === 'backoffice') {
      this.loadOlderBackofficeMessages();
      return;
    }

    this.loadOlderFrohokMessages();
  }

  private loadOlderCustomerMessages(): void {
    const guestUuid = this.getGuestUuid();

    if (!guestUuid || !this.customerHasMore || this.customerLoadingOlder || !this.customerNextCursor) {
      return;
    }

    this.customerLoadingOlder = true;
    this.socket.emit('loadOlderMessagesCustomer', {
      room_uuid: guestUuid,
      before_timestamp: this.customerNextCursor,
      chunk_size: this.customerChunkSize,
    });
  }

  private loadOlderBackofficeMessages(): void {
    const selectedRoom = sessionStorage.getItem('selectedRoom');
    const parsedRoom: IRoomLike = selectedRoom ? JSON.parse(selectedRoom) : {};

    if (!parsedRoom?.room_uuid || !this.backofficeHasMore || this.backofficeLoadingOlder || !this.backofficeNextCursor) {
      return;
    }

    this.backofficeLoadingOlder = true;
    this.socket.emit('loadOlderMessagesBackoffice', {
      room_uuid: parsedRoom.room_uuid,
      before_timestamp: this.backofficeNextCursor,
      chunk_size: this.backofficeChunkSize,
    });
  }

  private loadOlderFrohokMessages(): void {
    if (!this.selectedFrohokRoomUuid || this.frohokLoadingOlder) {
      return;
    }

    // Ha mar tudjuk, hogy nincs tobb elem, ne kuldjunk felesleges kerdest.
    if (this.frohokPaginationKnown && !this.frohokHasMore) {
      return;
    }

    const beforeTimestamp = this.frohokNextCursor || this.frohokData?.[0]?.chat_timestamp;
    if (!beforeTimestamp) {
      return;
    }

    this.frohokLoadingOlder = true;
    this.socket.emit('loadOlderMessagesFrohok', {
      room_uuid: this.selectedFrohokRoomUuid,
      before_timestamp: beforeTimestamp,
      offset: this.frohokData.length,
    });
  }

  private prependMessagesWithScroll(target: 'customer-facing' | 'backoffice' | 'FROhok', olderRows: IChatData[]): void {
    const chatContainer = target === 'customer-facing'
      ? this.customerScrollDown?.nativeElement
      : target === 'backoffice'
        ? this.backofficeScrollDown?.nativeElement
        : this.scrollDown?.nativeElement;
    const previousScrollHeight = chatContainer?.scrollHeight || 0;

    if (target === 'customer-facing') {
      this.customerFacingChatData = [...olderRows, ...this.customerFacingChatData];
    } else if (target === 'backoffice') {
      this.backofficeChatData = [...olderRows, ...this.backofficeChatData];
    } else {
      const hasBoundaryOverlap =
        olderRows.length > 0 &&
        this.frohokData.length > 0 &&
        olderRows[olderRows.length - 1].chat_timestamp === this.frohokData[0].chat_timestamp &&
        olderRows[olderRows.length - 1].sender === this.frohokData[0].sender &&
        olderRows[olderRows.length - 1].chat === this.frohokData[0].chat;

      const rowsToPrepend = hasBoundaryOverlap ? olderRows.slice(0, -1) : olderRows;
      this.frohokData = [...rowsToPrepend, ...this.frohokData];
    }

    if (!chatContainer) {
      return;
    }

    setTimeout(() => {
      const nextScrollHeight = chatContainer.scrollHeight;
      chatContainer.scrollTop = nextScrollHeight - previousScrollHeight + chatContainer.scrollTop;
    }, 0);
  }

  private scrollToBottom(target: 'customer-facing' | 'backoffice'): void {
    setTimeout(() => {
      const chatContainer = target === 'customer-facing'
        ? this.customerScrollDown?.nativeElement
        : this.backofficeScrollDown?.nativeElement;

      if (!chatContainer) {
        return;
      }

      chatContainer.scrollTop = chatContainer.scrollHeight;
    }, 0);
  }

  private isNearBottom(container: HTMLElement | undefined, threshold: number = 64): boolean {
    if (!container) {
      return true;
    }

    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
  }

  private buildTypingPayload(): ITypingPayload | null {
    const sender = this.getCurrentTypingSender();

    if (this.platform === 'customer-facing') {
      const roomUuid = this.getGuestUuid();
      return {
        room_uuid: roomUuid,
        chat: this.messageCtrl.value,
        sender,
        platform: 'customer-facing',
      };
    }

    if (this.platform === 'backoffice') {
      const selectedRoom = this.getSelectedBackofficeRoom();
      return {
        room_uuid: selectedRoom?.room_uuid || '',
        chat: this.messageCtrl.value,
        sender,
        platform: 'backoffice',
      };
    }

    const fallbackRoom: IRoomLike = this.frohokRooms[this.userIndex] || {};
    return {
      room_uuid: this.selectedFrohokRoomUuid || fallbackRoom.room_uuid || '',
      room_name: this.selectedFrohokRoomName || fallbackRoom.room_name || '',
      chat: this.messageCtrl.value,
      sender,
      platform: 'FROhok',
    };
  }

  private getCurrentTypingSender(): string {
    if (this.platform === 'customer-facing') {
      return this.customerFacingNameCtrl.value || '';
    }

    if (this.platform === 'backoffice') {
      return this.userInfo?.Name || '';
    }

    return this.frohokUser?.Name || '';
  }

  private handleTypingStatus(payload: ITypingStatus): void {
    if (!payload?.sender || !this.isTypingPayloadForCurrentRoom(payload)) {
      return;
    }

    if (this.sameIdentity(payload.sender, this.getCurrentTypingSender())) {
      return;
    }

    const displayName = payload.sender.trim();
    const senderKey = this.normalizeIdentity(displayName);
    if (!senderKey) {
      return;
    }

    const existingIndex = this.typingUsers.findIndex(name => this.sameIdentity(name, displayName));
    if (existingIndex >= 0) {
      this.typingUsers[existingIndex] = displayName;
    } else {
      this.typingUsers = [...this.typingUsers, displayName];
    }

    const existingTimer = this.typingUserTimers.get(senderKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.typingUsers = this.typingUsers.filter(name => !this.sameIdentity(name, displayName));
      this.typingUserTimers.delete(senderKey);
    }, 2000);

    this.typingUserTimers.set(senderKey, timer);
  }

  private isTypingPayloadForCurrentRoom(payload: ITypingStatus): boolean {
    if (!payload?.room_uuid) {
      return false;
    }

    if (this.platform === 'customer-facing') {
      const guestUuid = this.getGuestUuid();
      return !!guestUuid && guestUuid === payload.room_uuid;
    }

    if (this.platform === 'backoffice') {
      const selectedRoom = this.getSelectedBackofficeRoom();
      return !!selectedRoom?.room_uuid && selectedRoom.room_uuid === payload.room_uuid;
    }

    return !!this.selectedFrohokRoomUuid && this.selectedFrohokRoomUuid === payload.room_uuid;
  }

  private clearTypingUsers(): void {
    this.typingUsers = [];
    this.typingUserTimers.forEach(timer => clearTimeout(timer));
    this.typingUserTimers.clear();
  }

  /**
   * Egységes guest UUID olvasas localStorage/sessionStorage visszafele kompatibilis fallbackgel.
   */
  private getGuestUuid(): string {
    const localGuestUuid = localStorage.getItem('guestUuid');
    if (localGuestUuid) {
      return localGuestUuid;
    }

    const sessionGuestUuid = sessionStorage.getItem('guestUuid');
    if (sessionGuestUuid) {
      localStorage.setItem('guestUuid', sessionGuestUuid);
      return sessionGuestUuid;
    }

    return '';
  }

  /**
   * Egységes guest UUID tarolas localStorage-ban, sessionStorage mirrorral.
   */
  private setGuestUuid(guestUuid: string): void {
    localStorage.setItem('guestUuid', guestUuid);
    sessionStorage.setItem('guestUuid', guestUuid);
  }

  /**
   * Guest UUID teljes torlese mindket tarolobol.
   */
  private clearGuestUuid(): void {
    localStorage.removeItem('guestUuid');
    sessionStorage.removeItem('guestUuid');
  }
}

//#region -- interfaces --
interface ICreateRoom {
  room_uuid: string,
  room_name: string,
  room_type: string,
  subscriber: string,
  hotel_id: number,
  guest_name: string,
  reservationsGuestname?: any
  reservationsRoomid?: any
  reservationsArrival?: any
  reservationsDeparture?: any;
  resnum: string
}

interface IChatData {
  sender: string,
  text: string,
  chat: string,
  chat_timestamp: Date,
  frohokUnreadHighlight?: boolean
}

interface IMessageChunkMeta {
  room_uuid: string;
  target: 'customer-facing' | 'backoffice' | 'FROhok';
  has_more: boolean;
  next_cursor: Date | string | null;
  chunk_size: number;
}

interface IOlderMessagesChunk extends IMessageChunkMeta {
  rows: IChatData[];
}

interface IMessageDelta {
  room_uuid: string;
  room_name: string;
  message: IChatData;
}

interface ITypingPayload {
  room_uuid: string;
  room_name?: string;
  chat: string;
  sender: string;
  platform: 'customer-facing' | 'backoffice' | 'FROhok';
}

interface ITypingStatus {
  room_uuid: string;
  room_name: string;
  sender: string;
  platform: 'customer-facing' | 'backoffice' | 'FROhok';
}

interface IMessageReadByReception {
  room_uuid: string;
  read_at: Date | string;
}

interface IReadedCounter {
  room_uuid: string,
  counter: string,
}

interface IRoomLike {
  room_uuid?: string;
  room_name?: string;
  guest_name?: string;
  message_counter?: number | string;
  counter?: number | string;
  hiddenBagde?: boolean;
  reservation_departure?: string | Date;
  resnum?: string
}

interface IFrohokRoom extends IRoomLike {
  room_uuid: string;
  room_name: string;
  guest_name?: string;
}

//#endregion